import React, { useState } from "react";
import { useNotifications } from "../../hooks/useNotifications";
import CustomPagination from "../../common/Pagination";
import ConfirmationModal from "../../common/ConfirmationModal";
import { TableStatus } from "../../components/TableStatus";
import NotificationModal from "./NotificationModal";

export default function NotificationTablePage() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const {
    notifications,
    pagination,
    isLoading,
    isError,
    error,
    createNotification,
    updateNotification,
    deleteNotification,
    sendNotification,
    isDeleting,
    isCreating,
    isUpdating,
    isSending,
  } = useNotifications(currentPage, itemsPerPage);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSendMode, setIsSendMode] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  const handleOpenModal = (notification = null, sendMode = false) => {
    setIsSendMode(sendMode);
    setEditingNotification(notification);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (formData) => {
    try {
      if (isSendMode) {
        await sendNotification({ text: formData.text });
      } else if (editingNotification) {
        await updateNotification({
          id: editingNotification._id,
          data: formData,
        });
      } else {
        await createNotification(formData);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Operation failed", error);
    }
  };

  const handleDelete = async () => {
    if (!notificationToDelete) return;
    await deleteNotification(notificationToDelete._id);
    setNotificationToDelete(null);
    if (notifications.length === 1 && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const isActionLoading = isCreating || isUpdating || isSending;

  return (
    <>
      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
          <h4 className="mb-0 text-primary-emphasis">Push Notification Management</h4>

          <div className="d-flex gap-2">
            <button
              className="btn btn-primary btn-sm mr-2"
              onClick={() => handleOpenModal(null, true)}
            >
              <i className="fas fa-paper-plane mr-1"></i> Send Notification
            </button>
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleOpenModal()}
            >
              <i className="fas fa-plus me-1"></i> Add Notification
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>No</th>
                  <th>Text</th>
                  <th>Sort Order</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <TableStatus
                  status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                  error={error}
                  dataLength={notifications.length}
                  colSpan={4}
                  emptyText="No notifications found."
                />

                {!isLoading &&
                  notifications.map((item, index) => (
                    <tr key={item._id}>
                      <td>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td>{item?.text}</td>
                      <td>{item?.sort}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary mr-2"
                            onClick={() => handleOpenModal(item)}
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setNotificationToDelete(item)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {pagination?.totalPages > 1 && (
          <div className="card-footer bg-white">
            <CustomPagination
              currentPage={Number(currentPage)}
              totalPages={Number(pagination.totalPages)}
              totalItems={Number(pagination.totalRecords)}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      <NotificationModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleModalSubmit}
        initialData={editingNotification}
        isSendMode={isSendMode}
        isLoading={isActionLoading}
      />

      <ConfirmationModal
        show={notificationToDelete !== null}
        onClose={() => setNotificationToDelete(null)}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Confirm Deletion"
        confirmText="Delete"
        confirmButtonVariant="danger"
      >
        Are you sure you want to delete this notification?
      </ConfirmationModal>
    </>
  );
}